import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Popover } from './popover';

interface DateOfBirthPickerProps {
  label?: string;
  icon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  value?: string; // 'YYYY-MM-DD'
  onChange: (value: string) => void;
  error?: string;
  className?: string;
  readOnly?: boolean;
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const parseValue = (value?: string): Date | null => {
  if (!value) return null;
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const formatValue = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const formatDisplay = (d: Date): string =>
  d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });

// A native <input type="date"> hands date-of-birth entry entirely to the OS/browser's own
// widget. That's fine on desktop (Chrome's date input shows a quick month/year dropdown), but
// on Android it opens a full-screen calendar with nothing but Prev/Next month arrows — jumping
// back to a birth year like 1985 means tapping the arrow dozens of times. This renders our own
// compact calendar with Month/Year <select>s (native selects, so still a fast wheel-picker on
// mobile) for an instant jump to any year, consistently across every platform.
export const DateOfBirthPicker: React.FC<DateOfBirthPickerProps> = ({
  label, icon, rightIcon, value, onChange, error, className, readOnly,
}) => {
  const selected = useMemo(() => parseValue(value), [value]);
  const today = new Date();
  const currentYear = today.getFullYear();
  // Covers ages 0–100, which is plenty for a date-of-birth field.
  const years = useMemo(() => Array.from({ length: 100 }, (_, i) => currentYear - i), [currentYear]);

  // Defaults the visible month/year to a plausible adult age when nothing is picked yet,
  // rather than starting on the current month (which would need the most arrow-taps to leave).
  const [viewYear, setViewYear] = useState(selected?.getFullYear() ?? currentYear - 25);
  const [viewMonth, setViewMonth] = useState(selected?.getMonth() ?? 0);
  const [pendingDay, setPendingDay] = useState<Date | null>(selected);

  // A Popover anchored to the trigger works fine on desktop, but this field usually sits in
  // the right-hand column of a 2-col form grid — on a narrow phone screen that anchors a
  // 280px-wide calendar right at the edge of the viewport, clipping it. Below the `sm`
  // breakpoint, render it as a centered fixed overlay instead, same as the app's other modals.
  const [isMobile, setIsMobile] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 640px)');
    setIsMobile(mql.matches);
    const handleChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener('change', handleChange);
    return () => mql.removeEventListener('change', handleChange);
  }, []);

  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const leadingBlanks = new Date(viewYear, viewMonth, 1).getDay();
  const isCurrentViewMonth = viewYear === currentYear && viewMonth === today.getMonth();

  const resetViewToValue = () => {
    setViewYear(selected?.getFullYear() ?? currentYear - 25);
    setViewMonth(selected?.getMonth() ?? 0);
    setPendingDay(selected);
  };

  if (readOnly) {
    return (
      <div className={`flex flex-col gap-2 md:gap-1 ${className || ''}`}>
        {label && <label className="text-[14px] font-bold text-[#121212]">{label}</label>}
        <div
          className={`relative flex items-center w-full h-[56px] border rounded p-4 text-[12px] md:text-[14px] bg-[#F8F8F8] cursor-not-allowed ${
            icon ? 'pl-10' : ''
          } ${rightIcon ? 'pr-10' : ''} ${error ? 'border-[#FF0101]' : 'border-[#E5E7EB]'}`}
        >
          {icon && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">{icon}</span>}
          <span className={selected ? 'text-[#121212]' : 'text-gray-400'}>
            {selected ? formatDisplay(selected) : 'Select date'}
          </span>
          {rightIcon && <span className="absolute right-3 top-1/2 -translate-y-1/2">{rightIcon}</span>}
        </div>
        {error && <span className="text-[#FF0101] text-[12px] mt-1">{error}</span>}
      </div>
    );
  }

  const renderCalendar = (close: () => void) => (
    <div className="p-4 w-[280px] max-w-full">
      <div className="flex items-center gap-2 mb-3">
        <button
          type="button"
          onClick={() => {
            const prev = new Date(viewYear, viewMonth - 1, 1);
            setViewYear(prev.getFullYear());
            setViewMonth(prev.getMonth());
          }}
          className="p-1 rounded-full hover:bg-gray-100 text-gray-500 flex-shrink-0"
          aria-label="Previous month"
        >
          <ChevronLeft size={16} />
        </button>

        <select
          value={viewMonth}
          onChange={(e) => setViewMonth(Number(e.target.value))}
          className="flex-1 min-w-0 text-sm font-semibold text-[#121212] border border-gray-200 rounded-md px-2 py-1 outline-none"
        >
          {MONTHS.map((m, i) => (
            <option key={m} value={i}>{m}</option>
          ))}
        </select>

        <select
          value={viewYear}
          onChange={(e) => setViewYear(Number(e.target.value))}
          className="text-sm font-semibold text-[#121212] border border-gray-200 rounded-md px-2 py-1 outline-none"
        >
          {years.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>

        <button
          type="button"
          onClick={() => {
            const next = new Date(viewYear, viewMonth + 1, 1);
            if (next > today) return;
            setViewYear(next.getFullYear());
            setViewMonth(next.getMonth());
          }}
          disabled={isCurrentViewMonth}
          className="p-1 rounded-full hover:bg-gray-100 text-gray-500 disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0"
          aria-label="Next month"
        >
          <ChevronRight size={16} />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-1">
        {WEEKDAYS.map((d, i) => (
          <div key={i} className="text-[11px] text-gray-400 text-center font-medium">{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {Array.from({ length: leadingBlanks }).map((_, i) => (
          <div key={`blank-${i}`} />
        ))}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const d = new Date(viewYear, viewMonth, day);
          const future = d > today;
          const isPending = !!pendingDay && pendingDay.getTime() === d.getTime();
          return (
            <button
              type="button"
              key={day}
              disabled={future}
              onClick={() => setPendingDay(d)}
              className={`h-8 w-8 text-xs flex items-center justify-center rounded-full mx-auto transition-colors
                ${future ? 'text-gray-300 cursor-not-allowed' : 'text-[#121212] hover:bg-emerald-50'}
                ${isPending ? '!bg-emerald-500 !text-white' : ''}
              `}
            >
              {day}
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
        <button
          type="button"
          onClick={() => {
            setPendingDay(null);
            onChange('');
            close();
          }}
          className="text-xs font-medium text-gray-500 px-3 py-1.5 rounded-full hover:bg-gray-50"
        >
          Clear
        </button>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={close}
            className="text-xs font-medium text-gray-500 px-3 py-1.5 rounded-full hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              if (!pendingDay) return;
              onChange(formatValue(pendingDay));
              close();
            }}
            disabled={!pendingDay}
            className="text-xs font-semibold text-white bg-black px-3 py-1.5 rounded-full disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Set
          </button>
        </div>
      </div>
    </div>
  );

  const trigger = (onClick: () => void) => (
    <button
      type="button"
      onClick={() => {
        resetViewToValue();
        onClick();
      }}
      className={`relative flex items-center w-full h-[56px] border rounded p-4 outline-none text-[12px] md:text-[14px] transition text-left ${
        error ? 'border-[#FF0101]' : 'border-[#E5E7EB]'
      } ${icon ? 'pl-10' : ''} ${rightIcon ? 'pr-10' : ''}`}
    >
      {icon && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">{icon}</span>}
      <span className={selected ? 'text-[#121212]' : 'text-gray-400'}>
        {selected ? formatDisplay(selected) : 'Select date'}
      </span>
      {rightIcon && <span className="absolute right-3 top-1/2 -translate-y-1/2">{rightIcon}</span>}
    </button>
  );

  return (
    <div className={`flex flex-col gap-2 md:gap-1 ${className || ''}`}>
      {label && <label className="text-[14px] font-bold text-[#121212]">{label}</label>}

      {isMobile ? (
        <>
          {trigger(() => setMobileOpen(true))}
          {mobileOpen && (
            <div
              className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
              onClick={() => setMobileOpen(false)}
            >
              <div
                className="bg-white rounded-2xl shadow-lg border border-[#DBDEF0] overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                {renderCalendar(() => setMobileOpen(false))}
              </div>
            </div>
          )}
        </>
      ) : (
        <Popover
          className="w-full"
          align="left"
          trigger={({ toggle }) => trigger(toggle)}
          menuClassName="bg-white rounded-lg shadow-lg border border-[#DBDEF0] overflow-hidden"
        >
          {(close) => renderCalendar(close)}
        </Popover>
      )}

      {error && <span className="text-[#FF0101] text-[12px] mt-1">{error}</span>}
    </div>
  );
};
